import styled from '@emotion/styled';
import { Typography } from '@material-ui/core';
import { ClassAttributes, createElement } from 'react';
import { FormattedMessage } from 'react-intl';

import { DesignInspirationCase } from '../../../graphql/generated/graphql';
import { SPACING_REGULAR, SPACING_UNIT } from '../../constant/spacing';
import { gridLayoutContainer, gridLayoutItem } from '../../styleUtility/gridLayout';
import AspectRatioFixed from '../primitive/AspectRatioFixed';
import LfToBr from '../primitive/LfToBr';
import VideoScreen from '../primitive/VideoScreen';

export interface Props {
  designInspirationCase: Pick<DesignInspirationCase, 'thumbnailUrl' | 'url' | 'name' | 'description'>;
  className?: string;
}

const ProjectDesignInspiration = ({ designInspirationCase, className }: ClassAttributes<HTMLElement> & Props) => {
  const thumbnailImage = designInspirationCase.thumbnailUrl || '/assets/empty_thumbnail.png';

  const sampleScreen = designInspirationCase.url ? (
    <AspectRatioFixed aspectRatio={[4, 3]}>
      <VideoScreen src={designInspirationCase.url} controls nodownload />
    </AspectRatioFixed>
  ) : (
    <ThumbnailImage src={thumbnailImage} />
  );

  return (
    <Root className={className}>
      <Heading variant="h6">
        <FormattedMessage id="project_designInspiration.heading" />
      </Heading>

      <SampleScreen>{sampleScreen}</SampleScreen>

      <Title variant="h5">{designInspirationCase.name}</Title>

      <Description variant="body1">
        <LfToBr text={designInspirationCase.description || ''} />
      </Description>
    </Root>
  );
};

export const Root = styled('div')(
  gridLayoutContainer({
    columns: ['1fr', `${SPACING_UNIT * 3}px`, '1fr'],
    rows: ['auto', 'auto', '1fr'],
    areas: [
      ['heading', 'heading', 'heading'],
      ['sample', '.', 'title'],
      ['sample', '.', 'description'],
    ],
  }),
  {
    marginBottom: `${SPACING_UNIT * 3}px`,
  },
);

const Heading = styled(Typography)(gridLayoutItem('heading'), {
  marginBottom: SPACING_UNIT * 3,
  display: 'flex',
  justifyContent: 'space-between',
});

const SampleScreen = styled('div')(gridLayoutItem('sample', { justifySelf: 'center' }), { width: '100%' });

const ThumbnailImage = styled('img')({ maxWidth: '100%' });

const Title = styled(Typography)(gridLayoutItem('title'), { marginBottom: SPACING_REGULAR });

const Description = styled(Typography)(gridLayoutItem('description'));

export default ProjectDesignInspiration;
